import { useState } from "react";
import {
  registerInitialValues,
  registerValidationSchema,
  otpMobileInitialValues,
  otpMobileValidationSchema,
} from "@/constants/Auth";
import { Field, Form, Formik, ErrorMessage } from "formik";
import {
  AiOutlineEye,
  AiOutlineCheck,
  AiOutlineMail,
  AiOutlineEyeInvisible,
} from "react-icons/ai";
import { TextError } from "../Helpers/InputContainer";
import { toast } from "react-hot-toast";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { BiPhoneCall } from "react-icons/bi";
import { registerApiRoute } from "@/services/register";
import { event_mobile_number, event_user_signup } from "@/utils/gtm";
import Link from "next/link";
import {
  sendOtpByPhoneAndEmail,
  verifyOTPByPhone,
} from "@/services/register/apiBack";
import {useTurnstile} from 'react-turnstile'
import TurnstileRecaptcha from "@/components/turnstile/TurnstileRecaptcha";

function Register({ setLogin }) {
  const { t } = useTranslation("common");
  const { locale: lang } = useRouter();
  const [accept, setAccept] = useState(false);
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const turnstile = useTurnstile()

  const [verifyToken, setVerifyToken] = useState(null)
  const [registerToken, setRegisterToken] = useState(null)

  // verify the otp and register the user
  const handleSubmitRegister = async (
    { otp },
    { resetForm, setSubmitting }
  ) => {
    if (!registrationData) return;

    setSubmitting(true);
    try {
      const { data, error } = await verifyOTPByPhone(
        undefined,
        registrationData.phone_number,
        otp,
        lang,
        verifyToken,

      );

      if (error || data?.success === "false") {
        toast.error(
          data?.errormsg?.includes("Invalid")
            ? lang === "en"
              ? data?.errormsg
              : "الرمز غير صحيح"
            : lang === "en"
            ? "The attempt failed, please try again."
            : "فشلة المحاوله برجاء المحاوله مره اخرى"
        );
        setSubmitting(false);
        turnstile.reset();
        setVerifyToken(null)
        return;
      }

      // Proceed with registration
      const updatedData = {
        ...registrationData,
        profile_picture: "string",
        dateOfBirth: "2001-01-01",
        gender: "1",
        phone_number: "966".concat(registrationData.phone_number.slice(1)),
        recaptchaToken: registerToken,
      };
      delete updatedData.token;

      try {
        await registerApiRoute(updatedData);

        const { firstName, lastName, email, phone_number } = updatedData;
        event_user_signup(firstName, lastName, phone_number, email, null, null);

        toast.success(t("LoginWithNewAccount"));
        toast.success(t("otpSuccessBanner"));
        setLogin(true);

        // Reset states and form
        setRegistrationData(null);
        resetForm();
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          (lang === "en"
            ? "Registration failed. Please try again."
            : "فشل التسجيل. يرجى المحاولة مرة أخرى.");
        toast.error(errorMessage);
        // Only reset the OTP field, keep registration data
        setShowOtp(false);
        setVerifyToken(null)
        setRegisterToken(null)
        turnstile.reset();
        resetForm({ ...registrationData, otp: "" });
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      toast.error(
        lang === "en"
          ? "An error occurred. Please try again."
          : "حدث خطأ. يرجى المحاولة مرة أخرى."
      );
      setVerifyToken(null)
      setRegisterToken(null)
    } finally {
      setSubmitting(false);
    }
  };

  // send the otp to the phone number and show the otp form
  const handleSubmitSendOtp = async (values, { setSubmitting, setFieldValue }) => {
    setSubmitting(true);
    try {
      // First send OTP
      const { data } = await sendOtpByPhoneAndEmail(
        undefined,
        values.email,
        values.phone_number,
        values.token
      );
      if (data?.success === "true") {
        // Store registration data and show OTP form
        setRegistrationData(values);
        setShowOtp(true);
        toast.success(
          lang === "en"
            ? `OTP sent successfully to your phone number: ${values.phone_number}`
            : `تم إرسال رمز التحقق إلى رقم الهاتف: ${values.phone_number}`
        );
      } else {
        toast.error(
          data?.errormsg ||
            (lang === "en"
              ? `here is already an account registered with this phone number: ${values.phone_number}`
              : `هناك حساب مسجل بالفعل بهذا الرقم: ${values.phone_number}`)
        );
        turnstile.reset();
        setFieldValue("token", "");
      }
    } catch (error) {
      toast.error(
        lang === "en"
          ? "An error occurred. Please try again."
          : "حدث خطأ. يرجى المحاولة مرة أخرى."
      );
      turnstile.reset();
      setFieldValue("token", "");
    } finally {
      setSubmitting(false);
    }
  };

  if (showOtp) {
    return (
      <Formik
        initialValues={otpMobileInitialValues}
        validationSchema={otpMobileValidationSchema(t)}
        onSubmit={handleSubmitRegister}
      >
        {({ isSubmitting }) => (
          <Form>
            <div className="mb-2">
              <label
                className="block mb-1 font-semibold cursor-pointer"
                htmlFor="otp_number"
              >
                {t("Otp")}
              </label>
              <div className="relative">
                <Field
                  name="otp"
                  className="w-full p-2 border rounded-md outline-0"
                  placeholder={t("Otp")}
                  id="otp_number"
                />
              </div>
              <ErrorMessage name="otp" component={TextError} />
            </div>
            {!verifyToken ? <TurnstileRecaptcha
              action="verify-otp"
              className="flex items-center justify-center p-2 col-span-full"
              onVerify={(value) => {
                setVerifyToken(value)
              }}
            /> : <TurnstileRecaptcha
            action="register-submit"
            className="flex items-center justify-center p-2 col-span-full"
            onVerify={(value) => {
              setRegisterToken(value)
            }}
          />}
            <button
              disabled={isSubmitting || !verifyToken || !registerToken}
              type="submit"
              className="w-full p-2 font-semibold text-white rounded-md disabled:cursor-not-allowed bg-blue campaign:bg-campaignAccent border-red hover:bg-lightred"
            >
              {t(isSubmitting ? "Sending" : "Verify")}
            </button>
          </Form>
        )}
      </Formik>
    );
  }

  return (
    <Formik
      initialValues={registerInitialValues}
      validationSchema={registerValidationSchema(t)}
      onSubmit={(values, { setSubmitting, setFieldValue }) => {
        handleSubmitSendOtp(values, { setSubmitting, setFieldValue });
      }}
    >
      {({ isSubmitting, setFieldValue }) => (
        <Form className="grid grid-cols-2 gap-3">
          <div className="max-[450px]:col-span-2">
            <label
              htmlFor="first-name"
              className="block w-full mb-1 font-semibold cursor-pointer"
            >
              {t("FirstName")}
            </label>
            <Field
              minLength={3}
              className="w-full p-2 border rounded-md outline-0"
              placeholder={t("FirstName")}
              id="first-name"
              name="firstName"
            />
            <ErrorMessage name="firstName" component={TextError} />
          </div>
          <div className="max-[450px]:col-span-2">
            <label
              htmlFor="last-name"
              className="block w-full mb-1 font-semibold cursor-pointer"
            >
              {t("LastName")}
            </label>
            <Field
              minLength={3}
              className="w-full p-2 border rounded-md outline-0"
              placeholder={t("LastName")}
              id="last-name"
              name="lastName"
            />
            <ErrorMessage name="lastName" component={TextError} />
          </div>
          <div className="col-span-2">
            <label
              htmlFor="email"
              className="block w-full mb-1 font-semibold cursor-pointer"
            >
              {t("Email")}
            </label>
            <div className="relative">
              <Field
                type="email"
                className="w-full p-2 border rounded-md outline-0"
                placeholder={t("Email")}
                id="email"
                name="email"
              />
              <AiOutlineMail
                size={24}
                className={`absolute top-1/2 -translate-y-1/2 ${
                  lang === "en" ? "right-1" : "left-1"
                } text-zinc-400 cursor-pointer`}
              />
            </div>
            <ErrorMessage name="email" component={TextError} />
          </div>
          <div className="col-span-2">
            <label
              htmlFor="phone_number"
              className="block w-full mb-1 font-semibold cursor-pointer"
            >
              {t("telephoneInput")}
            </label>
            <div className="relative">
              <Field
                type="tel"
                className={`w-full p-2 outline-0 rounded-md border ${
                  lang === "ar" ? "text-end" : "text-start"
                }`}
                placeholder={t("telephoneInput")}
                id="phone_number"
                name="phone_number"
                onChange={({ target }) => {
                  setFieldValue("phone_number", target?.value);
                  if (
                    target.value?.length === 10 &&
                    target.value?.startsWith("05")
                  ) {
                    const mobile = "966".concat(target.value.slice(1));
                    event_mobile_number(mobile);
                  }
                }}
              />
              <BiPhoneCall
                size={24}
                className={`absolute top-1/2 -translate-y-1/2 ${
                  lang === "en" ? "right-1" : "left-1"
                } text-zinc-400 cursor-pointer`}
              />
            </div>
            <ErrorMessage name="phone_number" component={TextError} />
          </div>
          <div className="col-span-2">
            <label
              htmlFor="password"
              className="block w-full mb-1 font-semibold cursor-pointer"
            >
              {t("Password")}
            </label>
            <div className="relative">
              <Field
                type={!show ? "password" : "text"}
                className="w-full p-2 border rounded-md outline-0"
                placeholder={t("Password")}
                id="password"
                name="password"
              />
              {!show ? (
                <AiOutlineEye
                  size={24}
                  onClick={() => setShow(!show)}
                  className={`absolute top-1/2 -translate-y-1/2 ${
                    lang === "en" ? "right-1" : "left-1"
                  } text-zinc-400 cursor-pointer`}
                />
              ) : (
                <AiOutlineEyeInvisible
                  size={24}
                  onClick={() => setShow(!show)}
                  className={`absolute top-1/2 -translate-y-1/2 ${
                    lang === "en" ? "right-1" : "left-1"
                  } text-zinc-400 cursor-pointer`}
                />
              )}
            </div>
            <ErrorMessage name="password" component={TextError} />
          </div>
          <div className="relative col-span-2">
            <label
              htmlFor="rep-password"
              className="block w-full mb-1 font-semibold cursor-pointer"
            >
              {t("ConfirmPassword")}
            </label>
            <div className="relative">
              <Field
                type={!showConfirm ? "password" : "text"}
                className="w-full p-2 border rounded-md outline-0"
                placeholder={t("ConfirmPassword")}
                id="rep-password"
                name="confirmPassword"
              />
              {!showConfirm ? (
                <AiOutlineEye
                  size={24}
                  onClick={() => setShowConfirm(!showConfirm)}
                  className={`absolute top-1/2 -translate-y-1/2 ${
                    lang === "en" ? "right-1" : "left-1"
                  } text-zinc-400 cursor-pointer`}
                />
              ) : (
                <AiOutlineEyeInvisible
                  size={24}
                  onClick={() => setShowConfirm(!show)}
                  className={`absolute top-1/2 -translate-y-1/2 ${
                    lang === "en" ? "right-1" : "left-1"
                  } text-zinc-400 cursor-pointer`}
                />
              )}
            </div>
            <ErrorMessage name="confirmPassword" component={TextError} />
          </div>
          <div className="flex items-center col-span-2">
            <div
              onClick={() => {
                setFieldValue("terms", !accept === false ? "" : !accept);
                setAccept(!accept);
              }}
              className="relative w-4 h-4 bg-white border border-gray-300 rounded-sm"
            >
              {accept && (
                <AiOutlineCheck
                  size={13}
                  className="absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 text-red"
                />
              )}
            </div>
            <Field type="checkbox" name="terms" className="hidden" />
            <span
              onClick={() => {
                setFieldValue("terms", !accept === false ? "" : !accept);
                setAccept(!accept);
              }}
              className="mx-2 cursor-pointer text-zinc-600"
            >
              {t("AcceptTerms")} {` `}
            </span>
            <Link
              href="/static/terms-conditions"
              className="underline cursor-pointer text-red"
            >
              {t("Terms")}
            </Link>
          </div>
          <ErrorMessage
            name="terms"
            component="p"
            className="-mt-2 text-sm text-red"
          />
          <TurnstileRecaptcha
            action="register-send-otp"
            className="flex items-center justify-center p-2 col-span-full"
            onVerify={(value) => {
              setFieldValue("token", value);
            }}
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full col-span-2 p-2 font-bold text-white rounded-md bg-blue campaign:bg-campaignAccent hover:bg-lightred disabled:cursor-not-allowed"
          >
            {t(isSubmitting ? "Logining" : "Register")}
          </button>
        </Form>
      )}
    </Formik>
  );
}

export default Register;
